import ElementController from "../base-controllers/ElementController.js";
/**
 * App Sidebar controller — manages the mobile off-canvas sidebar.
 *
 * On mobile (< --md / 768px) the sidebar starts translated off-screen.
 * This controller handles:
 *
 * - Opening/closing via [data-sidebar-trigger] buttons
 * - Backdrop overlay (click to close)
 * - Escape key to close
 * - Body scroll lock when open
 * - Auto-close when resizing past the desktop breakpoint
 * - Close on link click (mobile navigation)
 *
 * HTML contract:
 *
 *   <aside data-slot="sidebar"
 *          data-controller="AppSidebar"
 *          aria-label="Navigeringsmeny">
 *     <nav data-slot="sidebar-content">...</nav>
 *     <div data-slot="sidebar-footer">...</div>
 *   </aside>
 *
 *   <!-- Triggers (anywhere in the page): -->
 *   <button data-sidebar-trigger aria-label="Meny">...</button>
 */
class AppSidebar extends ElementController {
    constructor() {
        super(...arguments);
        this.backdrop = null;
        this.mql = null;
        this.triggers = [];
        // -- Public API ----------------------------------------------------------
        this.toggle = (event) => {
            if (this.isOpen) {
                this.close(event);
            }
            else {
                this.open();
            }
        };
        this.open = () => {
            if (this.isOpen)
                return;
            this.root.setAttribute("data-open", "");
            if (this.backdrop) {
                this.backdrop.style.display = "block";
                this.backdrop.setAttribute("data-visible", "");
            }
            document.body.style.overflow = "hidden";
            // Update trigger aria state.
            for (const trigger of this.triggers) {
                trigger.setAttribute("aria-expanded", "true");
            }
        };
        this.close = (event) => {
            if (!this.isOpen)
                return;
            this.root.removeAttribute("data-open");
            if (this.backdrop) {
                this.backdrop.removeAttribute("data-visible");
                this.backdrop.style.display = "";
            }
            document.body.style.overflow = "";
            // Return keyboard focus, but do not focus a button after a backdrop tap.
            for (const trigger of this.triggers) {
                trigger.setAttribute("aria-expanded", "false");
            }
            const pointerDismissal = event?.type === "click" && (event.currentTarget === this.backdrop || event.detail > 0 || !!event.pointerType);
            if (pointerDismissal) {
                // The backdrop cannot be keyboard-activated. Do not rely on click.detail:
                // touch dismissal can retain text-field modality even with a zero detail.
                const focused = document.activeElement;
                if (focused instanceof HTMLElement && (this.root.contains(focused) || this.triggers.includes(focused)))
                    focused.blur();
            }
            else {
                this.triggers[0]?.focus({ preventScroll: true });
            }
        };
        // -- Event handlers ------------------------------------------------------
        this.handleKeydown = (event) => {
            if (event.key === "Escape" && this.isOpen) {
                event.preventDefault();
                this.close();
            }
        };
        /** Close sidebar when viewport becomes desktop-sized. */
        this.handleBreakpoint = (event) => {
            if (event.matches && this.isOpen) {
                this.close();
            }
        };
        /** Close sidebar on link click (mobile navigation). */
        this.handleLinkClick = (event) => {
            if (this.isOpen) {
                this.close(event);
            }
        };
    }
    get isOpen() {
        return this.root.hasAttribute("data-open");
    }
    init() {
        // Create the backdrop element and insert it after the sidebar.
        this.backdrop = document.createElement("div");
        this.backdrop.setAttribute("data-slot", "sidebar-backdrop");
        this.root.after(this.backdrop);
        // Bind all trigger buttons in the page.
        this.triggers = Array.from(document.querySelectorAll("[data-sidebar-trigger]"));
        for (const trigger of this.triggers) {
            this.on(trigger, "click", this.toggle);
        }
        // Backdrop click closes the sidebar.
        this.on(this.backdrop, "click", this.close);
        // Escape key closes the sidebar.
        this.on(document, "keydown", this.handleKeydown);
        // Auto-close when the viewport crosses the desktop breakpoint.
        this.mql = window.matchMedia("(min-width: 769px)");
        this.mql.addEventListener("change", this.handleBreakpoint);
    }
    destroy() {
        this.backdrop?.remove();
        this.mql?.removeEventListener("change", this.handleBreakpoint);
        if (this.isOpen) {
            document.body.style.overflow = "";
        }
    }
}
AppSidebar.id = "AppSidebar";
AppSidebar.binds = {
    root: [["click", "handleLinkClick", { delegate: "a[href]" }]],
};
export default AppSidebar;
//# sourceMappingURL=AppSidebar.js.map